import { ContentModalContentType, ContentTypeForComparison } from 'domains/content/types';
import { Folder } from 'models/types';
import useArchiveContent from './mutations/useArchiveContent';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from './queries/keys';

type Props = {
    activeItem: ContentModalContentType | Folder;
    refetchTableData: () => void;
    resetActiveItem: () => void;
    setErrorMessage: (error: string) => void;
    setSuccessMessage: (message: string) => void;
};

const useArchiveTableItem = ({
    activeItem,
    refetchTableData,
    resetActiveItem,
    setErrorMessage,
    setSuccessMessage,
}: Props) => {
    const queryClient = useQueryClient();

    const onError = (error: Error) => {
        setErrorMessage(error.message);
        resetActiveItem();
    };

    const onSuccess = () => {
        setSuccessMessage(`"${activeItem?.name}" successfully archived.`);
        refetchTableData();
        resetActiveItem();

        // Invalidate content and archived content queries to refresh the lists
        queryClient.invalidateQueries(QueryKeys.CONTENT());
        queryClient.invalidateQueries(QueryKeys.ARCHIVED_CONTENT);
        // Also invalidate all content queries with different parameters to ensure both original and search queries are updated
        queryClient.invalidateQueries(['content']);
    };

    const getContentTypeForApi = (type: string): string => {
        switch (type) {
            case ContentTypeForComparison.EMAIL:
                return 'email';
            case ContentTypeForComparison.SMS:
                return 'sms';
            case ContentTypeForComparison.LANDING_PAGE:
                return 'landing_page';
            case ContentTypeForComparison.DIRECT_MAIL:
                return 'direct_mail';
            case ContentTypeForComparison.VOICE:
                return 'voice';
            default:
                return type.toLowerCase();
        }
    };

    const { isLoading, mutate: archiveContent, isSuccess } = useArchiveContent({
        onError,
        onSuccess,
    });

    const getArchiveItemFunction = () => {
        if (!activeItem) return () => {};

        const archiveData = {
            id: Number(activeItem.id),
            type: getContentTypeForApi(activeItem.type),
        };

        return () => archiveContent(archiveData);
    };

    return { getArchiveItemFunction, isLoading, isSuccess };
};

export default useArchiveTableItem;
